import { Injectable } from '@angular/core';
import { forkJoin, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { AsientoModal } from '../../domain/tipo-asiento-modal.model';
import { API_CONFIG, BaseCrudService, PageRequest, PageResponse } from '@nebula-shared/data-access';
import { ordenarEnMemoria, type OrdenDelDialogo } from '../../../select-dialog/orden-dialogo';

@Injectable({ providedIn: 'root' })
export class TipoAsientosService extends BaseCrudService<AsientoModal> {
  constructor() {
    super();
    const config = API_CONFIG.accounting;
    this.setBaseUrl(`${config.baseUrl}${config.endpoints.tiposAsiento}`);
  }

  /**
   * Método para hacer la petición POST al endpoint page-response
   * de tipos de asiento
   */
  private postPageResponse(page = 0, size = 10, searchTerm = ''): Observable<any> {
    const body: PageRequest = {
      page: page,
      size: size,
      searchsBy: [],
      ordersBy: [],
    };

    if (searchTerm && searchTerm.trim() !== '') {
      body.searchsBy = [
        { key: 'nombre', value: searchTerm, operation: 'CONTAINS' as const },
        { key: 'codigo', value: searchTerm, operation: 'CONTAINS' as const },
      ];
    }

    return this.http.post<any>(`${this.baseUrl}/page-response`, body);
  }

  /**
   * Extraer datos de la respuesta del API
   */
  private extractDataFromResponse(response: any): any {
    const data = response?.response?.content || response?.content || response;
    return {
      content: data?.content || [],
      totalElements: data?.totalElements || 0,
      totalPages: data?.totalPages || 0,
      size: data?.size || 10,
      number: data?.number || 0,
    };
  }

  /**
   * Obtener todos los tipos de asiento
   */
  getAllAsientos(): Observable<AsientoModal[]> {
    return this.postPageResponse(0, 10).pipe(
      switchMap((response) => {
        const data = this.extractDataFromResponse(response);
        const totalPages = data.totalPages;
        const pageSize = data.size;

        if (totalPages <= 1) {
          return this.mapContent(data.content);
        }

        const requests$: Observable<any>[] = [];
        for (let page = 1; page < totalPages; page++) {
          requests$.push(this.postPageResponse(page, pageSize));
        }

        return forkJoin(requests$).pipe(
          switchMap((responses) => {
            let allContent = [...data.content];
            responses.forEach((res) => {
              const pageData = this.extractDataFromResponse(res);
              allContent = [...allContent, ...pageData.content];
            });
            return this.mapContent(allContent);
          }),
        );
      }),
    );
  }

  /**
   * Mapear el contenido a AsientoModal[]
   */
  private mapContent(content: any[]): Observable<AsientoModal[]> {
    const mapped = content.map((item: any) => ({
      id: item.id,
      codigo: item.codigo || item.codigoAsiento || '',
      nombre: item.nombre || item.nombreAsiento || '',
      descripcion: item.descripcion || '',
      estado: item.estado || item.estadoAsiento || 'ACTIVO',
      tipo: item.tipo || '',
      ...item,
    }));
    return new Observable((observer) => {
      observer.next(mapped);
      observer.complete();
    });
  }

  /**
   * Obtener tipos de asiento paginados con búsqueda
   */
  getAsientosPaginados(
    page = 0,
    size = 10,
    searchTerm = '',
    soloActivos = true,
    orden?: OrdenDelDialogo,
  ): Observable<PageResponse<AsientoModal>> {
    return this.getAllAsientos().pipe(
      map((allAsientos) => {
        let filtered = allAsientos;

        if (searchTerm && searchTerm.trim() !== '') {
          const term = searchTerm.toLowerCase().trim();
          filtered = filtered.filter(
            (asiento) =>
              asiento.codigo?.toLowerCase().includes(term) ||
              asiento.nombre?.toLowerCase().includes(term) ||
              asiento.descripcion?.toLowerCase().includes(term),
          );
        }

        if (soloActivos) {
          filtered = filtered.filter((asiento) => asiento.estado === 'ACTIVO');
        }

        filtered.sort((a, b) => a.codigo.localeCompare(b.codigo));
        // El orden elegido en el dialogo se aplica a la lista completa (Mantis #392).
        filtered = ordenarEnMemoria(filtered, orden);

        const start = page * size;
        const end = start + size;
        const paginatedContent = filtered.slice(start, end);

        return {
          content: paginatedContent,
          totalElements: filtered.length,
          totalPages: Math.ceil(filtered.length / size),
          size: size,
          number: page,
          numberOfElements: paginatedContent.length,
          first: page === 0,
          last: end >= filtered.length,
          empty: paginatedContent.length === 0,
        } as PageResponse<AsientoModal>;
      }),
    );
  }
}
